import React, { useEffect, useMemo, useState } from 'react';
import {
	DataGridBody,
	DataGridRow,
	DataGrid,
	DataGridHeader,
	DataGridHeaderCell,
	DataGridCell,
	TableColumnDefinition,
	Field,
	SearchBox,
	makeStyles,
	SearchBoxChangeEvent,
	InputOnChangeData,
	OnSelectionChangeData,
	tokens,
	DataGridProps
} from "@fluentui/react-components";
import type { ICustomer } from '../IDB';
import { Server } from '../apis';

export type SortState = Parameters<NonNullable<DataGridProps["onSortChange"]>>[1];

interface ICustomerSearchProps {
	columns: TableColumnDefinition<ICustomer>[];
	defaultSortState?: SortState;
	filterResults?: (customer: ICustomer) => boolean;
	onSelected: (custoemr: ICustomer) => void;
	prepopulateSearch?: boolean;
	preselectedValue?: string;
	searchLicenses?: boolean;
	sortable: boolean;
	containerRef?: React.RefObject<HTMLDivElement>;
}

const useStyles = makeStyles({
	search: {
		marginBottom: tokens.spacingVerticalM,
		fontWeight: 'bold'
	}
});

let stillTypingTimeoutSearch: NodeJS.Timeout;
export const CustomerSearch = (props: ICustomerSearchProps) => {
	const styles = useStyles();
	const server = useMemo(() => Server.getInstance(), []);
	const [searchResults, setResults] = useState<ICustomer[]>([]);

	const performSearch = async (search: string, limit?: number, page?: number): Promise<ICustomer[]> => {
		let results: ICustomer[] = [];
		if (search === "" && limit && page) {
			results = await server.getCustomers(limit, page);
		} else {
			const customerResults = await server.getCustomers(limit ?? null, page ?? null, search);

			if (props.searchLicenses) {
				// Duplicate customer results for each child license
				// and make licenses an array of 1
				for (let cr = 0; cr < customerResults.length; cr += 1) {
					const cust = customerResults[cr];
					if (cust.licenses.length > 0) {
						for (let l = 0; l < cust.licenses.length; l += 1) {
							const lic = cust.licenses[l];
							if (lic.code.toLowerCase() === search) {
								// if exactly equals, only show this result
								results = [
									{
										...cust,
										licenses: [lic]
									}
								];
								break;
							}
							results.push(
								{
									...cust,
									licenses: [lic]
								}
							);
						}
					} else {
						results.push(cust);
					}
				}
			} else {
				results = customerResults;
			}
		}

		if (props.filterResults) {
			results = results.filter(props.filterResults);
		}

		return results;
	};

	const onSearch = async (event: SearchBoxChangeEvent, data: InputOnChangeData) => {
		clearTimeout(stillTypingTimeoutSearch);
		stillTypingTimeoutSearch = setTimeout(async (): Promise<void> => {

			const searchValue = data.value.toLowerCase().trim();
			if (searchValue === "" && !props.prepopulateSearch) {
				setResults([]);
				return;
			}

			const results = await performSearch(searchValue);
			setResults(results);
		}, 500);
	};

	useEffect(() => {
		let searching: boolean = false;
		let searchPage = 1;
		const init = async () => {
			if (props.prepopulateSearch) {
				const initResults = await performSearch("", 50, 1);
				setResults(initResults);
				if (props.containerRef) {
					const container = props.containerRef.current;
					container?.addEventListener('scroll', async () => {
						if ((container.scrollTop + container.offsetHeight >= container.scrollHeight - 150) && searching === false) {
							searching = true;
							// Load next page of results
							searchPage++;
							const results = await performSearch("", 50, searchPage);
							setResults((prev) => [...prev, ...results]);
							searching = false;
						}
					});
				}
			}
		};
		init();
	}, [props.prepopulateSearch]);

	const onSelection = (e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>, data: OnSelectionChangeData) => {
		props.onSelected(searchResults[data.selectedItems.values().next().value]);
	};

	let defaultSelection = undefined;
	if (props.preselectedValue) {
		for (let sr = 0; sr < searchResults.length; sr += 1) {
			if (searchResults[sr].id === Number(props.preselectedValue) || (searchResults[sr].licenses.length > 0 && searchResults[sr].licenses[0].code === props.preselectedValue)) {
				defaultSelection = new Set([sr]);
			}
		}
	}

	return (
		<>
			<Field label="Search for Customer" className={styles.search} required>
				<SearchBox onChange={onSearch} placeholder="Start typing to search or paste full license code" appearance='filled-darker' autoComplete='false' />
			</Field>
			{searchResults.length === 0 ? "No matching results" :
				<DataGrid
					columns={props.columns}
					key={defaultSelection ? '1' : '0'}
					focusMode="composite"
					items={searchResults}
					onSelectionChange={onSelection}
					selectionMode="single"
					sortable={props.sortable}
					defaultSortState={props.defaultSortState}
					defaultSelectedItems={defaultSelection}
				>
					<DataGridHeader>
						<DataGridRow
							selectionCell={{
								checkboxIndicator: { "aria-label": "Select all rows" },
							}}
						>
							{({ renderHeaderCell }) => (
								<DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
							)}
						</DataGridRow>
					</DataGridHeader>
					<DataGridBody<ICustomer>>
						{({ item, rowId }) => (
							<DataGridRow<ICustomer>
								key={rowId}
								selectionCell={{
									checkboxIndicator: { "aria-label": "Select row" },
								}}
							>
								{({ renderCell }) => (
									<DataGridCell>{renderCell(item)}</DataGridCell>
								)}
							</DataGridRow>
						)}
					</DataGridBody>
				</DataGrid>
			}
		</>
	)
};